import { z } from 'zod'

import {
  CLIENT_ROLES,
  FORM_VALIDATION_ERROR_MESSAGES,
  OWNER_ROLES,
  READONLY_FIELD_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from '@/constants'
import {
  MenuItemType,
  OrganisationFieldPermissions,
  OrganisationInterface,
} from '@/types'
import { isClientUser } from '@/utils/auth/userRoles'

export const DEFAULT_MENU_ITEMS: MenuItemType[] = [
  {
    id: 1,
    name: 'Организация',
  },
  {
    id: 2,
    name: 'Пользователи',
  },
  {
    id: 3,
    name: 'Банковские счета',
  },
  {
    id: 4,
    name: 'Договоры',
  },
  {
    id: 5,
    name: 'Адреса доставки',
  },
  {
    id: 6,
    name: 'Отображение складов',
  },
]

const cleanStrNoTrim = (val: unknown): string =>
  String(val || '').replace(/[\u200B-\u200D\uFEFF]/g, '')

const { EMAIL_WRONG_FORMAT_MESSAGE } = FORM_VALIDATION_ERROR_MESSAGES

const getRefineError = (field: string, readonly?: boolean) => ({
  message: readonly ? READONLY_FIELD_MESSAGE : REQUIRED_FIELD_MESSAGE,
  path: [field],
})

export const defaultOrganisationEditSchema = z
  .object({
    description: z.preprocess(cleanStrNoTrim, z.string()),
    vatNumber: z.preprocess(cleanStrNoTrim, z.string()),
    fullLegalName: z.preprocess(cleanStrNoTrim, z.string()),
    shortLegalName: z.preprocess(cleanStrNoTrim, z.string()),
    legalAddress: z.preprocess(cleanStrNoTrim, z.string()),
    postalAddress: z.preprocess(cleanStrNoTrim, z.string()),
    phoneNumber: z.preprocess(cleanStrNoTrim, z.string()),
    contactPhone: z.preprocess(cleanStrNoTrim, z.string()),
    mainBankAccount: z
      .object({
        value: z.preprocess(cleanStrNoTrim, z.string()),
        label: z.preprocess(cleanStrNoTrim, z.string()),
      })
      .nullable()
      .optional(),
    email: z
      .preprocess(cleanStrNoTrim, z.string())
      .optional()
      .or(z.literal('')),
    emailDistribution: z
      .preprocess(cleanStrNoTrim, z.string())
      .optional()
      .or(z.literal('')),
    signatoryFullName: z.preprocess(cleanStrNoTrim, z.string()),
    signatoryPosition: z.preprocess(cleanStrNoTrim, z.string()),
    signatoryOperatesUnder: z.preprocess(cleanStrNoTrim, z.string()),
    supervisorManager: z
      .object({
        value: z.preprocess(cleanStrNoTrim, z.string()),
        label: z.preprocess(cleanStrNoTrim, z.string()),
      })
      .nullable()
      .optional(),
    registrationDate: z.preprocess(cleanStrNoTrim, z.string()),
    ipRegistrationDate: z.preprocess(cleanStrNoTrim, z.string()),
  })
  .partial()

export type OrganisationEditType = z.infer<typeof defaultOrganisationEditSchema>

export const getBaseSchema = (
  organisationData: OrganisationInterface,
  permissions?: OrganisationFieldPermissions | null
) => {
  return defaultOrganisationEditSchema
    .refine(
      (data) => {
        if (permissions && !permissions.canEditVatNumber) return true
        if (!data.vatNumber || data.vatNumber === '') return true
        return /^\d{9}$/.test(data.vatNumber)
      },
      {
        message: 'УНП должен состоять из 9 цифр',
        path: ['vatNumber'],
      }
    )
    .refine(
      (data) => {
        if (permissions && !permissions.canEditEmail) return true
        if (!data.email || data.email === '') return true
        return data.email
          .split(',')
          .every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()))
      },
      { message: EMAIL_WRONG_FORMAT_MESSAGE, path: ['email'] }
    )
    .refine(
      (data) => {
        if (permissions && !permissions.canEditEmailDistribution) return true
        if (!data.emailDistribution || data.emailDistribution === '')
          return true
        return data.emailDistribution
          .split(',')
          .every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()))
      },
      { message: EMAIL_WRONG_FORMAT_MESSAGE, path: ['emailDistribution'] }
    )
    .refine(
      (data) =>
        !data.registrationDate ||
        data.registrationDate === data.registrationDate,
      getRefineError('registrationDate', true)
    )
    .refine(
      (data) =>
        !data.ipRegistrationDate ||
        data.ipRegistrationDate === data.ipRegistrationDate,
      getRefineError('ipRegistrationDate', true)
    )
}

const getSchemaForAllExceptAdminAndSuperuser = (
  organisationData: OrganisationInterface,
  permissions?: OrganisationFieldPermissions | null
) => {
  return getBaseSchema(organisationData, permissions)
}

const getSchemaForSuperuser = (
  organisationData: OrganisationInterface,
  permissions?: OrganisationFieldPermissions | null
) => {
  return getBaseSchema(organisationData, permissions)
}

export const getOrganisationEditSchema = (
  tokenRoles: readonly string[],
  organisationData: OrganisationInterface,
  permissions?: OrganisationFieldPermissions | null
) => {
  const hasRole = (role: string) => tokenRoles.includes(role)

  if (hasRole(OWNER_ROLES.ADMIN)) {
    return getBaseSchema(organisationData, permissions)
  }

  if (
    hasRole(OWNER_ROLES.SUPERMANAGER) ||
    hasRole(OWNER_ROLES.MANAGER) ||
    hasRole(OWNER_ROLES.SALESMAN)
  ) {
    return getSchemaForAllExceptAdminAndSuperuser(organisationData, permissions)
  }

  if (isClientUser(tokenRoles) && hasRole(CLIENT_ROLES.SUPERUSER)) {
    return getSchemaForSuperuser(organisationData, permissions)
  }

  return getBaseSchema(organisationData, permissions)
}
